import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users, UserPlus, MessageSquare, Clock, Mail, Send,
  ChevronRight, Loader2, RefreshCw, Search,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface Prospect {
  email: string;
  name: string;
  role: string;
  registered_at: string;
  message_count: number;
}

export function ProspectsTab() {
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const [contactProspect, setContactProspect] = useState<Prospect | null>(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sending, setSending] = useState(false);

  const fetchProspects = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/prospects/registered`, {
        credentials: "include" as RequestCredentials,
      });
      if (res.ok) {
        const data = await res.json();
        setProspects(data.prospects || []);
      }
    } catch {}
    setLoading(false);
  };

  const handleContactProspect = (p: Prospect) => {
    setContactProspect(p);
    setEmailSubject(`Grafton IA — ${p.name || "Bonjour"}`);
    setEmailBody("");
  };

  const handleSendToProspect = async () => {
    if (!contactProspect || !emailSubject.trim() || !emailBody.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/gmail/send`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: contactProspect.email,
          subject: emailSubject,
          body: emailBody,
        }),
      });
      if (res.ok) {
        toast.success(`Email envoyé à ${contactProspect.name || contactProspect.email}`);
        setContactProspect(null);
        setEmailSubject("");
        setEmailBody("");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur d'envoi");
      }
    } catch {
      toast.error("Erreur réseau");
    }
    setSending(false);
  };

  useEffect(() => {
    fetchProspects();
  }, []);

  const filtered = search
    ? prospects.filter(
        (p) =>
          p.email.toLowerCase().includes(search.toLowerCase()) ||
          (p.name || "").toLowerCase().includes(search.toLowerCase())
      )
    : prospects;

  const formatDate = (d: string) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "—";
    }
  };

  const getEngagementLevel = (count: number) => {
    if (count >= 10) return { label: "Très engagé", color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" };
    if (count >= 5) return { label: "Engagé", color: "bg-blue-500/10 text-blue-600 border-blue-500/20" };
    if (count >= 1) return { label: "Découverte", color: "bg-amber-500/10 text-amber-600 border-amber-500/20" };
    return { label: "Inscrit", color: "bg-muted text-muted-foreground border-border" };
  };

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{prospects.length}</p>
                <p className="text-xs text-muted-foreground">Total inscrits</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {prospects.filter((p) => p.message_count >= 5).length}
                </p>
                <p className="text-xs text-muted-foreground">Engagés (5+ messages)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {prospects.filter((p) => {
                    if (!p.registered_at) return false;
                    const d = new Date(p.registered_at);
                    const now = new Date();
                    return (now.getTime() - d.getTime()) < 7 * 24 * 60 * 60 * 1000;
                  }).length}
                </p>
                <p className="text-xs text-muted-foreground">Nouveaux (7j)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <UserPlus className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {prospects.filter((p) => p.message_count === 0).length}
                </p>
                <p className="text-xs text-muted-foreground">Jamais interagi</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search + Refresh */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="prospects-search"
            placeholder="Rechercher par email ou nom..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>
        <Button variant="outline" size="sm" onClick={fetchProspects} disabled={loading} className="gap-1.5 text-xs">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Actualiser
        </Button>
      </div>

      {/* Prospects List */}
      <Card data-testid="prospects-list">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Utilisateurs enregistrés</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Aucun prospect trouvé</p>
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {filtered.map((p) => {
                const engagement = getEngagementLevel(p.message_count);
                return (
                  <div
                    key={p.email}
                    className="flex items-center gap-4 px-5 py-3.5 hover:bg-muted/10 transition-colors"
                    data-testid={`prospect-row-${p.email.replace(/[^a-zA-Z0-9]/g, '-')}`}
                  >
                    <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                      {(p.name || p.email).charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{p.name || "Sans nom"}</p>
                        {p.role === "admin" && (
                          <Badge variant="outline" className="text-[9px] border-primary/30 text-primary">Admin</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Mail className="h-3 w-3 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground truncate">{p.email}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <Badge variant="outline" className={`text-[10px] ${engagement.color}`}>
                        {engagement.label}
                      </Badge>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {p.message_count} message{p.message_count > 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] text-muted-foreground">{formatDate(p.registered_at)}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-[10px] h-7 gap-1 shrink-0"
                      onClick={(e) => { e.stopPropagation(); handleContactProspect(p); }}
                      data-testid={`contact-prospect-${p.email.replace(/[^a-zA-Z0-9]/g, '-')}`}
                    >
                      <Send className="h-3 w-3" /> Contacter
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact Prospect Dialog */}
      <Dialog open={!!contactProspect} onOpenChange={(open) => !open && setContactProspect(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-4 w-4 text-primary" /> Contacter {contactProspect?.name || "le prospect"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="text-sm text-muted-foreground">
              Destinataire : <span className="text-foreground font-medium">{contactProspect?.email}</span>
            </div>
            <Input
              placeholder="Objet"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              data-testid="prospect-email-subject"
            />
            <Textarea
              placeholder="Votre message..."
              rows={6}
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              data-testid="prospect-email-body"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setContactProspect(null)}>
                Annuler
              </Button>
              <Button
                size="sm"
                className="gap-1.5"
                onClick={handleSendToProspect}
                disabled={sending || !emailSubject.trim() || !emailBody.trim()}
                data-testid="prospect-email-send"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Envoyer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
